import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { environment } from '../../environments/environment';
import { UiConfiguration } from '@models/interfaces/configuration/UiConfiguration.interface';
import { UiService } from './ui.service';
import {
  NestedStrings,
  TranslationJson,
} from '@models/types/components/Language.type';

@Injectable({
  providedIn: 'root',
})
export class TranslationService {
  private readonly http: HttpClient = inject(HttpClient);
  private readonly _bucket = environment.contentUploaderApi.bucketName;
  private readonly _folder = 'translations/v3';
  private readonly _latestFileName = 'sb3_translations_latest.json';

  public readonly availableLanguages: WritableSignal<string[]> = signal<string[]>([]);
  public readonly generalTexts: WritableSignal<TranslationJson> = signal<TranslationJson>({});
  private readonly uiconfig: Signal<UiConfiguration> =
    inject(UiService).uiConfig;

  public readonly currentLanguage = computed(
    () => this.uiconfig().Languages.defaultLang ?? 'en'
  );

  get folder() {
    return this._folder;
  }

  get backupFolder() {
    return `${this.folder}/backups`;
  }

  get latestFile() {
    return this._folder + '/' + this._latestFileName;
  }

  public getLatestFile() {
    const dir = 'https://' + this._bucket + '/' + this.latestFile;
    return this.http.get<TranslationJson>(dir);
  }

  public fetchGeneralTexts() {
    this.getLatestFile().subscribe({
      next: (res) => {
        this.generalTexts.set(res as TranslationJson);
        this.availableLanguages.set(Object.keys(res));
      },
      error: (err) => {
        console.error('Error fetching file:', err);
      },
    });
  }

  public getText(
    section: keyof NestedStrings,
    key: keyof NestedStrings,
    defaultValue = ''
  ): string {
    const sectionObj = this.generalTexts()[this.currentLanguage()]?.[section];
    if (!sectionObj || typeof sectionObj === 'string') return defaultValue;

    const value = sectionObj[key];
    return typeof value === 'string' ? value : defaultValue;
  }
}
